import clsx from "clsx";

import { BagApiBadRequestError, isBagApiBadRequestError } from "@repo/bag";
import * as AsyncAction from "@repo/ui/async-action";
import { useBag } from "@repo/ui/bag";
import { STRINGS } from "@/constants";
import { SpinnerIcon } from "@/theme/icons/spinner";
import { TYPOGRAPHY_LABEL3 } from "@/theme/topshop/typography/typography-constants";

import classes from "./button.module.css";

type AddToBagButtonProps = {
  variantId: number | null;
  isInStock: boolean;
  onClick?: () => void;
  onError: (error: string) => void;
  onSuccess?: (variantId: number) => void;
};

export const AddToBagButton = ({
  variantId,
  onClick,
  onError,
  onSuccess,
  isInStock,
}: AddToBagButtonProps) => {
  const { addProduct } = useBag();

  const handleAddToBag = async () => {
    onClick?.();

    if (typeof variantId !== "number" || Number.isNaN(variantId)) {
      onError(STRINGS.addToBag.errors.selectOptions);

      return false;
    }

    try {
      await addProduct({ variantId });
    } catch (error) {
      const message = isBagApiBadRequestError(error)
        ? (error as BagApiBadRequestError).bagErrorMessage
        : STRINGS.addToBag.errors.failedToAdd;

      onError(message);
      return false;
    }

    onSuccess?.(variantId);

    return true;
  };

  const idleText = isInStock
    ? STRINGS.addToBag.addToBag
    : STRINGS.addToBag.outOfStock;

  return (
    <AsyncAction.Root successTimeoutMs={3000}>
      <AsyncAction.Button
        variant="primary"
        size="large"
        alignment="start"
        fullWidth
        className={clsx(classes.button, TYPOGRAPHY_LABEL3)}
        onClick={handleAddToBag}
        disabled={!isInStock}
      >
        <AsyncAction.Idle>{idleText}</AsyncAction.Idle>
        <AsyncAction.Error>{idleText}</AsyncAction.Error>
        <AsyncAction.Loading>
          <SpinnerIcon label={STRINGS.addToBag.addingToBag} />
        </AsyncAction.Loading>
        <AsyncAction.Success>{STRINGS.addToBag.addedToBag}</AsyncAction.Success>
      </AsyncAction.Button>
    </AsyncAction.Root>
  );
};
